import Controller from '@ember/controller';
import { computed, setProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { importSync } from '@embroider/macros';
import { ensureSafeComponent } from '@embroider/util';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';

/**
 * @param {Event} e
 */
const cancelScrollWheel = (e) => {
  if (e.target.matches('input[type=number]')) {
    e.target.blur();
  }
};

const scrollWindowOnTransition = (transition) => {
  if (!transition.data.preserveScrollPosition) {
    window.scrollTo(0, 0);
  }
};

const hideLayoutRoutes = new Set([
  'signout',
  'admin.masquerade',
  'end-masquerade',
  undefined,
  null,
]);

const showSimpleHeaderRoutes = new Set([
  'deactivated',
  'delinquent',
  'joincompany',
  'joincompany_loading',
]);

const showLogoContainerRoutes = new Set([
  'signin',
  'signin.index',
  'reset-password',
]);

const hideFooterRoutes = new Set([
  'joincompany',
  'joincompany_loading',
  'signin',
  'signin.index',
  'reset-password',
  'maintenance',
  'out',
]);

class ApplicationController extends Controller {
  /**
   * Register Services
   */
  @service('states/application') applicationState;
  @service('state-managers/application') applicationStateManager;
  @service router;
  @service session;

  /**
   * Set to true to show the Posting Creation / Editing header.  Must also set showHeader to false to hide the main header.
   * @param {Boolean=}
   */
  isShowCreatePostingHeader = false;

  /**
   * Set this to trow and 'showHeader' to false to show a 'simple header'.  This has only a logo and text you can
   * specify by setting the simpleHeaderText param.
   *
   * @param {Boolean=}
   */
  isShowSimpleHeader = false;

  /**
   * @param {String}      Name of the modal component
   */
  modalComponent = null;

  /**
   * @param {Object}     Model for the modal component
   */
  modalModel = null;

  /**
   * This is text that will be in the center of the header when setting the 'isShowSimpleHeader' flag to true
   *
   * @param {String=}
   */
  simpleHeaderText = '';

  /**
   * @param {String}      Text to add on the top right part of the simple header
   */
  simpleHeaderRightText = '';

  /**
   * @param {Function}    Pass in a function along with the simpleHeaderRightText to add an action when the right top
   *                      text is clicked.
   */
  simpleHeaderRightAction = null;

  /**
   * @type {Ember.Component}
   */
  postingsCarousel = null;

  /** @type {Ember.Component} */
  profileOverlay = null;

  /**
   * Set to TRUE when on maintenance route
   *
   * @param {Boolean}
   */
  @computed('router.currentRouteName')
  get maintenanceModeActive() {
    return this.router.currentRouteName === EMBER_ROUTE_NAMES.MAINTENANCE;
  }

  @computed('router.currentRouteName')
  get canShowLayout() {
    const { currentRouteName } = this.router;

    return (
      !hideLayoutRoutes.has(currentRouteName) &&
      !currentRouteName.startsWith('iframe.')
    );
  }

  @computed('isShowSimpleHeader', 'router.currentRouteName')
  get showSimpleHeader() {
    const { currentRouteName } = this.router;

    return (
      this.isShowSimpleHeader ||
      showSimpleHeaderRoutes.has(currentRouteName) ||
      currentRouteName.startsWith('signup.') ||
      currentRouteName.startsWith('brand.')
    );
  }

  @computed('router.currentRouteName')
  get showLogoContainer() {
    return showLogoContainerRoutes.has(this.router.currentRouteName);
  }

  @computed('router.currentRouteName')
  get showFooter() {
    const { currentRouteName } = this.router;

    const hide =
      hideFooterRoutes.has(currentRouteName) ||
      currentRouteName.startsWith('signup.') ||
      currentRouteName.startsWith('brand.') ||
      currentRouteName.startsWith('message-center.');

    return !hide;
  }

  constructor() {
    super(...arguments);

    document.addEventListener('wheel', cancelScrollWheel, {
      passive: true,
    });

    this.router.on('routeDidChange', scrollWindowOnTransition);
  }

  /** @inheritDoc */
  willDestroy() {
    super.willDestroy(...arguments);
    document.removeEventListener('wheel', cancelScrollWheel);
    this.router.off('routeDidChange', scrollWindowOnTransition);
  }

  openModal(modalOrComponentString, modalModel = {}) {
    if (typeof modalOrComponentString === 'string') {
      const safeModalOrComponentString = modalOrComponentString.replace(/\./, '/');
      const module = importSync(`../components/modals/${safeModalOrComponentString}`);
      const modalComponent = ensureSafeComponent(module.default, this);

      setProperties(this, {
        modalComponent,
        modalModel,
      });

      return;
    }

    setProperties(this, {
      modalComponent: modalOrComponentString,
      modalModel,
    });
  }

  closeModal() {
    setProperties(this, {
      modalComponent: null,
      modalModel: null,
    });
  }
}

export default ApplicationController;
